'use client';

import { useTranslations } from 'next-intl';
import useFilter from 'hooks/use-filter';
import { useLoaderOverlay } from 'app/[lang]/[esite]/contexts/loader-overlay-context';
import { useRef, useState } from 'react';
import { createNewBoltLengthFacet } from '../utils';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { FacetRange } from 'services/internalSearch/types';
import { CatButton, CatSlider } from 'components/blocks-components';
import { BlocksSlider } from '@blocks-web-components/components/slider/slider';
import { BlocksSliderChangeEvent } from '@blocks-web-components/events';
import { CatIconArrowDown } from '@blocks-react/components/Icons/ArrowDown';
import style from './range-slider.module.scss';
import cx from 'classnames';

type RangeSliderProps = {
  label: string;
  minMaxValues: FacetRange;
};

type MinMaxValues = {
  minValue: number | undefined;
  maxValue: number | undefined;
};

export const RangeSlider = ({ label, minMaxValues }: RangeSliderProps) => {
  const t = useTranslations();
  const { startTransition } = useLoaderOverlay();
  const { handleSortByFacet } = useFilter();
  const [sliderChanged, setSliderChanged] = useState(false);
  const [shouldClearFilters, setShouldClearFilters] = useState(false);
  const [sliderValues, setSliderValues] = useState<MinMaxValues>({
    minValue: minMaxValues.min,
    maxValue: minMaxValues.max
  });
  const rangeSliderRef = useRef<HTMLDivElement | null>(null);

  const getInput = (slider: BlocksSlider, classname: string) => {
    const blocksInput = slider?.shadowRoot?.querySelector(classname);
    return blocksInput?.shadowRoot?.querySelector('input');
  };

  const getBoltLengthFacet = () =>
    createNewBoltLengthFacet(sliderValues.minValue, sliderValues.maxValue);

  const handleOnSliderChange = (e: BlocksSliderChangeEvent) => {
    e.preventDefault();
    const slider = rangeSliderRef?.current?.querySelector('cat-slider');

    const minInput = getInput(slider, '.cat-c-slider__output-range-one');
    const maxInput = getInput(slider, '.cat-c-slider__output-range-two');

    if (
      isEmpty(minInput?.value) ||
      isEmpty(maxInput?.value) ||
      Number(minInput?.value) < minMaxValues.min ||
      Number(maxInput?.value) < minMaxValues.min ||
      Number(minInput?.value) > minMaxValues.max ||
      Number(maxInput?.value) > minMaxValues.max
    ) {
      setSliderChanged(false);
    } else {
      setSliderChanged(true);
      setSliderValues({
        minValue: e.detail.minValue,
        maxValue: e.detail.maxValue
      });
    }
  };

  const handleOnApplyFilters = () => {
    startTransition(() => {
      handleSortByFacet(getBoltLengthFacet(), true);
      setShouldClearFilters(true);
    });
  };

  const handleOnClearFilters = () => {
    startTransition(() => {
      handleSortByFacet(getBoltLengthFacet(), false);
      setShouldClearFilters(false);
      setSliderChanged(false);
      setSliderValues({
        minValue: minMaxValues.min,
        maxValue: minMaxValues.max
      });
    });
  };

  return (
    <div className={style['range-slider']}>
      <div
        className="position-relative"
        id="range-slider-element"
        ref={rangeSliderRef}
      >
        <CatSlider
          behavior="range"
          outputPosition="bottom"
          data-testid="range-slider"
          onBlSliderChange={handleOnSliderChange}
          displayOutputField
          hideMinMaxLabel
          label={label}
          lowerRangeAriaLabel={t('MINIMUM_VALUE_PREFERENCES_PAGE')}
          upperRangeAriaLabel={t('MAXIMUM_VALUE_PREFERENCES_PAGE')}
          min={minMaxValues.min}
          max={minMaxValues.max}
          maxValue={sliderValues.maxValue}
          minValue={sliderValues.minValue}
          minLabel={t('MINIMUM_VALUE_PREFERENCES_PAGE')}
          maxLabel={t('MAXIMUM_VALUE_PREFERENCES_PAGE')}
          step={10} // This is going to be changed in a future story after Product defines the correct way to determine the interval for the slider
        />

        {sliderChanged && !shouldClearFilters && (
          <div
            className={cx('position-absolute', style['range-slider__arrow'])}
          >
            <CatIconArrowDown data-testid="arrow-icon" />
          </div>
        )}

        {!shouldClearFilters ? (
          <CatButton
            variant="primary"
            size="sm"
            fullWidth
            disabled={!sliderChanged}
            onClick={handleOnApplyFilters}
          >
            {t('APPLY_CHANGES')}
          </CatButton>
        ) : (
          <CatButton
            variant="ghost"
            size="sm"
            fullWidth
            onClick={handleOnClearFilters}
          >
            {t('CLEAR_FILTERS')}
          </CatButton>
        )}
      </div>
    </div>
  );
};
